import {Grid, Heading, ProductCard} from '~/modules';
import {useCallback, useState} from 'react';

import {CollectionHero} from '~/modules/Layout/components/CollectionHero';
import {Container} from '~/modules/Layout/components/Container';
import InfiniteScroll from 'react-infinite-scroll-component';
import {PortalSpinner} from '~/modules/Common/components/PortalSpinner';
import {ProductsProvider} from '~/modules/Product/context/ProductsContext';
import {loader} from '~/routes/($expertName)/collections.$slug.$id';
import {useLoadCollection} from '../hooks/useLoadCollection';
import {useLoaderData} from '@remix-run/react';
import {useProducts} from '~/modules/Product/hooks/useProducts';

export default function CollectionPage() {
  const data = useLoaderData<typeof loader>();
  const {collection, products: initialProducts} = data || {};
  const [products, setProducts] = useState(initialProducts);
  const [shopifyProducts, setShopifyProducts] = useState(
    data?.shopifyProducts?.nodes || [],
  );
  const [page, setPage] = useState(2);
  const [limit] = useState(12);
  const loadCollection = useLoadCollection();
  const getCollection = useCallback(async () => {
    const result = await loadCollection(collection.id, page, limit);
    if (result.collection) {
      setProducts([...products, ...(result.products || [])]);
      setShopifyProducts([
        ...shopifyProducts,
        ...(result.shopifyProducts?.nodes || []),
      ]);
      setPage(page + 1);
    }
    return result;
  }, [collection.id, limit, loadCollection, page, products, shopifyProducts]);

  return !collection?.id && !collection?.name ? null : (
    <>
      <CollectionHero
        title={collection.name}
        subtitle={collection.description}
        image={{src: collection.bannerImageUrl, alt: ''}}
      />
      <Container usePadding>
        <Heading as="h2" size="bodyMd" className="font-bold">
          {collection.productNum || products.length} product
          {(collection.productNum || products.length) > 1 ? 's' : ''}
        </Heading>
        <ProductsProvider
          shopifyProducts={shopifyProducts}
          apiProducts={products}
        >
          <InfiniteScroll
            dataLength={products.length}
            next={getCollection}
            hasMore={products.length < collection.productNum}
            loader={
              <div className="center-row justify-center py-2">
                <PortalSpinner size="sm" />
              </div>
            }
            hasChildren={true}
            scrollThreshold={0.7}
          >
            <CollectionPageLayout />
          </InfiniteScroll>
        </ProductsProvider>
      </Container>
    </>
  );
}

const CollectionPageLayout = () => {
  const {products} = useProducts();
  return (
    <Grid layout="products">
      {products.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          className="flex-auto"
          visibleByDefault={true}
          theme="storefront"
        />
      ))}
    </Grid>
  );
};
